import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { setSentryUser } from '@/lib/sentry'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  login: (
    email: string,
    password: string,
    captchaToken?: string
  ) => Promise<{ error: AuthError | null }>
  signup: (
    email: string,
    password: string,
    captchaToken?: string
  ) => Promise<{ error: AuthError | null }>
  logout: () => Promise<void>
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: AuthError | null }>
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children, eager = true }: { children: ReactNode; eager?: boolean }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const applySession = useCallback((nextSession: Session | null) => {
    setSession(nextSession)
    setUser(nextSession?.user ?? null)

    if (nextSession?.user) {
      setSentryUser({
        id: nextSession.user.id,
        email: nextSession.user.email,
      })
      return
    }

    setSentryUser(null)
  }, [])

  const bootstrapAuth = useCallback(async () => {
    const { data: { session: nextSession } } = await supabase.auth.getSession()
    applySession(nextSession)
    setLoading(false)
  }, [applySession])

  useEffect(() => {
    let isDisposed = false
    let idleHandle: number | null = null
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null

    const startAuth = () => {
      void bootstrapAuth()
    }

    if (eager) {
      setLoading(true)
      startAuth()
    } else {
      setLoading(false)
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(() => {
          if (!isDisposed) {
            startAuth()
          }
        }, { timeout: 1200 })
      } else {
        timeoutHandle = setTimeout(() => {
          if (!isDisposed) {
            startAuth()
          }
        }, 1200)
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        applySession(nextSession)
        setLoading(false)
      }
    )

    return () => {
      isDisposed = true
      subscription.unsubscribe()
      if (idleHandle !== null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleHandle)
      }
      if (timeoutHandle) {
        clearTimeout(timeoutHandle)
      }
    }
  }, [applySession, bootstrapAuth, eager])

  const login = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        captchaToken,
      },
    })
    return { error }
  }, [])

  const signup = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        captchaToken,
      },
    })
    return { error }
  }, [])

  const logout = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
      captchaToken,
    })
    return { error }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })
    return { error }
  }, [])

  const contextValue = useMemo(
    () => ({
      user,
      session,
      loading,
      login,
      signup,
      logout,
      resetPassword,
      updatePassword,
    }),
    [user, session, loading, login, signup, logout, resetPassword, updatePassword]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
